/**
 * Login.jsx — Redesigné · Dark Luxury Fintech
 * Layout: Split screen (branding gauche / formulaire droite)
 * Accent: amber/gold (identité Pi Network)
 * Toute la logique métier originale est préservée
 * + Google Sign-In natif via Capacitor (fix APK WebView)
 */

import { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  GithubAuthProvider,
  signOut,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { auth, db } from "../firebase/config";
import { useNavigate } from "react-router-dom";
import {
  doc, getDoc, setDoc, serverTimestamp,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, Lock, Eye, EyeOff, Github,
  Shield, AlertCircle, Globe, CheckCircle,
} from "lucide-react";
// ✅ Import du logo personnalisé
import adminLogo from '../assets/logos/logo-admin.png';

// ─── Allowed domains (same list as original) ─────────────────────────────
const ALLOWED_DOMAINS = [
  "localhost",
  "sapi-54976.firebaseapp.com",
  "sapi-54976.web.app",
  "sapiadmin.web.app",
  "sapiadmin.firebaseapp.com",
  "sapi.etralis.com",
  "sapi-admin.etralis.com",
  "192.168.1.184",
];

// ─── Animated background orbs ─────────────────────────────────────────────
const Orbs = () => (
  <>
    <div style={{ position: "absolute", top: "-10%", left: "-10%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(245,158,11,.12) 0%, transparent 65%)", pointerEvents: "none" }} />
    <div style={{ position: "absolute", bottom: "-5%", right: "-5%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,.1) 0%, transparent 65%)", pointerEvents: "none" }} />
    <div style={{ position: "absolute", top: "40%", left: "30%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(34,211,238,.06) 0%, transparent 65%)", pointerEvents: "none" }} />
  </>
);

// ─── Input field ──────────────────────────────────────────────────────────
const InputField = ({
  id, label, type = "text", value, onChange,
  placeholder, icon: Icon, disabled, autoFocus,
  rightElement, error,
}) => (
  <div style={{ marginBottom: error ? 6 : 0 }}>
    <label htmlFor={id} style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: ".1em", display: "block", marginBottom: 6 }}>
      {label}
    </label>
    <div style={{ position: "relative" }}>
      {Icon && (
        <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <Icon size={15} color={error ? "#f87171" : "#475569"} strokeWidth={1.8} />
        </div>
      )}
      <input
        id={id} type={type} value={value} onChange={onChange}
        placeholder={placeholder} disabled={disabled} autoFocus={autoFocus}
        autoComplete={id}
        style={{
          width: "100%",
          background: "rgba(255,255,255,.04)",
          border: `1px solid ${error ? "rgba(239,68,68,.5)" : "rgba(255,255,255,.1)"}`,
          borderRadius: 10,
          padding: `10px ${rightElement ? "42px" : "12px"} 10px ${Icon ? "38px" : "12px"}`,
          color: "#e2e8f0",
          fontSize: 13,
          outline: "none",
          opacity: disabled ? .5 : 1,
          transition: "border-color .15s",
        }}
        onFocus={e => { if (!error) e.target.style.borderColor = "rgba(245,158,11,.5)"; }}
        onBlur={e => { if (!error) e.target.style.borderColor = "rgba(255,255,255,.1)"; }}
      />
      {rightElement && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
          {rightElement}
        </div>
      )}
    </div>
  </div>
);

// ─── Main Login component ─────────────────────────────────────────────────
const Login = () => {
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [debugInfo, setDebugInfo]       = useState("");
  const [loading, setLoading]           = useState(false);
  const [socialLoading, setSocialLoading] = useState({ google: false, github: false });
  const navigate = useNavigate();

  const hostname     = window.location.hostname;
  const isDomainOk   = ALLOWED_DOMAINS.includes(hostname);
  const isFormValid  = email.length > 0 && password.length >= 6;
  const anyLoading   = loading || socialLoading.google || socialLoading.github;
  const isNative     = Capacitor.isNativePlatform();

  // ── Check existing session ──────────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      const stored = localStorage.getItem("adminUser");
      if (!stored) return;
      try {
        const u = JSON.parse(stored);
        if (u.role === "Admin" && auth.currentUser) {
          const snap = await getDoc(doc(db, "users", u.uid));
          if (snap.exists() && snap.data().userRole === "Admin") navigate("/");
          else { await signOut(auth); localStorage.removeItem("adminUser"); }
        }
      } catch { localStorage.removeItem("adminUser"); }
    };
    check();
  }, [navigate]);

  // ── Email login ─────────────────────────────────────────────────────
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Veuillez saisir un email et un mot de passe."); return; }
    setLoading(true);
    setErrorMessage("");
    setDebugInfo(`Connexion depuis: ${hostname}`);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const snap = await getDoc(doc(db, "users", cred.user.uid));
      if (snap.exists()) {
        const data = snap.data();
        if (data.userRole === "Admin") {
          await setDoc(doc(db, "users", cred.user.uid), { lastLoginAt: serverTimestamp() }, { merge: true });
          localStorage.setItem("adminUser", JSON.stringify({ uid: cred.user.uid, email: cred.user.email, name: data.name || cred.user.email, role: data.userRole, lastLogin: new Date().toISOString() }));
          toast.success("Connexion administrateur réussie !");
          navigate("/");
        } else {
          await signOut(auth);
          setErrorMessage(`Accès refusé. Rôle détecté: ${data.userRole}. Seuls les administrateurs peuvent accéder.`);
          setDebugInfo(`Rôle: ${data.userRole} (Admin requis)`);
          toast.error("Accès administrateur requis");
        }
      } else {
        await signOut(auth);
        setErrorMessage("Compte non trouvé. Contactez un administrateur.");
        toast.error("Compte non enregistré");
      }
    } catch (err) {
      const MAP = {
        "auth/invalid-email":                    "Format d'email invalide.",
        "auth/user-disabled":                    "Ce compte a été désactivé.",
        "auth/user-not-found":                   "Aucun compte avec cet email.",
        "auth/wrong-password":                   "Mot de passe incorrect.",
        "auth/invalid-credential":               "Identifiants invalides. Vérifiez votre email et mot de passe.",
        "auth/too-many-requests":                "Trop de tentatives. Réessayez plus tard.",
        "auth/network-request-failed":           "Erreur réseau. Vérifiez votre connexion.",
        "auth/operation-not-allowed":            "Connexion email/mot de passe non activée.",
        "auth/unauthorized-domain":              "Domaine non autorisé pour l'authentification.",
      };
      const msg = MAP[err?.code] || err?.message || "Échec de la connexion.";
      setErrorMessage(msg);
      setDebugInfo(`Domaine: ${hostname} | Code: ${err?.code || "inconnu"}`);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  // ── Social login ────────────────────────────────────────────────────
  const handleSocialLogin = async (providerType) => {
    setSocialLoading(p => ({ ...p, [providerType]: true }));
    setErrorMessage("");

    try {
      let result;

      if (providerType === "google" && isNative) {
        // ── Chemin natif (APK Capacitor) : SDK Google natif, aucune WebView
        // impliquée dans l'échange OAuth → contourne le blocage Google
        const { credential } = await FirebaseAuthentication.signInWithGoogle();
        const authCredential = GoogleAuthProvider.credential(credential.idToken);
        result = await signInWithCredential(auth, authCredential);
      } else {
        // ── Chemin web classique (localhost / site déployé) — inchangé
        const provider = providerType === "google" ? new GoogleAuthProvider() : new GithubAuthProvider();
        if (providerType === "github") provider.addScope("user:email");
        result = await signInWithPopup(auth, provider);
      }

      const userRef = doc(db, "users", result.user.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data.userRole === "Admin") {
          await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
          localStorage.setItem("adminUser", JSON.stringify({ uid: result.user.uid, email: result.user.email, name: result.user.displayName || data.name || result.user.email, role: data.userRole, profilePic: result.user.photoURL || data.profilePic, lastLogin: new Date().toISOString() }));
          toast.success(`Connecté avec ${providerType === "google" ? "Google" : "GitHub"} !`);
          navigate("/");
        } else {
          await signOut(auth);
          throw new Error(`Votre rôle: ${data.userRole}. Accès réservé aux administrateurs.`);
        }
      } else {
        await setDoc(userRef, { uid: result.user.uid, email: result.user.email, name: result.user.displayName || "", profilePic: result.user.photoURL || "", createdAt: serverTimestamp(), userRole: "User", provider: providerType, lastLoginAttempt: serverTimestamp() });
        await signOut(auth);
        throw new Error("Compte créé. Contactez un administrateur pour obtenir l'accès.");
      }
    } catch (err) {
      const MAP = {
        "auth/popup-closed-by-user":             "La fenêtre de connexion a été fermée.",
        "auth/account-exists-with-different-credential": "Un compte existe déjà avec un autre fournisseur.",
        "auth/popup-blocked":                    "Popup bloquée. Autorisez les popups pour ce site.",
        "auth/cancelled-popup-request":          "La requête de popup a été annulée.",
        "auth/unauthorized-domain":              "Ce domaine n'est pas autorisé.",
        "auth/invalid-credential":               "Identifiants invalides.",
      };
      const msg = MAP[err?.code] || err?.message || `Échec de la connexion ${providerType}.`;
      setErrorMessage(msg);
      setDebugInfo(`Domaine: ${hostname} | Code: ${err?.code || "inconnu"} | Natif: ${isNative}`);
      toast.error(msg);
    } finally { setSocialLoading(p => ({ ...p, [providerType]: false })); }
  };

  // ─── RENDER ──────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#070d1a", display: "flex", fontFamily: "'DM Sans','Outfit',system-ui,sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes float { 0%,100% { transform:translateY(0) } 50% { transform:translateY(-12px) } }
        @keyframes pulse-ring { 0% { transform:scale(.9); opacity:.6 } 100% { transform:scale(1.3); opacity:0 } }
        input::placeholder { color: #334155 }
        * { box-sizing: border-box }
      `}</style>
      <Orbs />

      {/* ═══ LEFT — Branding panel ═══ */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: .6, ease: [.22, 1, .36, 1] }}
        style={{ display: "none", flex: 1, flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "60px 48px", position: "relative", borderRight: "1px solid rgba(255,255,255,.05)" }}
        className="login-left"
      >
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(245,158,11,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(245,158,11,.03) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }} />

        <div style={{ position: "relative", maxWidth: 420, textAlign: "center" }}>
          <div style={{ position: "relative", display: "inline-block", marginBottom: 32 }}>
            <div style={{ position: "absolute", inset: -8, borderRadius: "50%", border: "2px solid rgba(245,158,11,.2)", animation: "pulse-ring 2s cubic-bezier(.455,.03,.515,.955) infinite" }} />
            <div style={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#f59e0b,#d97706)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 40px rgba(245,158,11,.3)",
              overflow: "hidden",
            }}>
              {/* ✅ Logo personnalisé */}
              <img
                src={adminLogo}
                alt="Sapi Admin"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  padding: 8, // pour aérer l'icône
                }}
              />
            </div>
          </div>

          <h1 style={{ fontSize: 32, fontWeight: 800, color: "#fff", lineHeight: 1.15, marginBottom: 14 }}>
            Sapi Admin<br />
            <span style={{ background: "linear-gradient(135deg,#f59e0b,#fbbf24)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Pi Network</span>
          </h1>
          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 36 }}>
            Plateforme de gestion centralisée pour les boutiques, commandes, utilisateurs et paramètres du réseau Pi.
          </p>

          {[
            { icon: "🏪", label: "Gestion des Pi Stores" },
            { icon: "📦", label: "Suivi des commandes" },
            { icon: "👥", label: "Administration utilisateurs" },
            { icon: "🚚", label: "Paramètres de livraison" },
          ].map(({ icon, label }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 12, padding: "10px 14px", marginBottom: 10, textAlign: "left" }}
            >
              <span style={{ fontSize: 18 }}>{icon}</span>
              <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{label}</span>
              <CheckCircle size={14} style={{ marginLeft: "auto", flexShrink: 0 }} />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ═══ RIGHT — Form panel ═══ */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: .5 }}
        style={{ width: "100%", maxWidth: 480, margin: "auto", display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 32px", position: "relative" }}
      >
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#f59e0b,#d97706)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            boxShadow: "0 0 28px rgba(245,158,11,.28)",
            overflow: "hidden",
          }}>
            {/* ✅ Logo personnalisé */}
            <img
              src={adminLogo}
              alt="Sapi Admin"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                padding: 6,
              }}
            />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Panneau Administrateur</h2>
          <p style={{ fontSize: 12, color: "#475569" }}>Accès réservé au personnel autorisé</p>

          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, padding: "5px 12px", borderRadius: 20, background: isDomainOk ? "rgba(16,185,129,.1)" : "rgba(245,158,11,.1)", border: `1px solid ${isDomainOk ? "rgba(16,185,129,.3)" : "rgba(245,158,11,.3)"}` }}>
            <Globe size={11} color={isDomainOk ? "#34d399" : "#f59e0b"} />
            <span style={{ fontSize: 10, fontWeight: 600, color: isDomainOk ? "#34d399" : "#f59e0b", fontFamily: "monospace" }}>{hostname}</span>
            {isDomainOk
              ? <CheckCircle size={10} color="#34d399" />
              : <AlertCircle size={10} color="#f59e0b" />
            }
          </div>
        </div>

        <AnimatePresence>
          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              style={{ overflow: "hidden", marginBottom: 16 }}
            >
              <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <AlertCircle size={15} color="#f87171" style={{ marginTop: 1, flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: "#f87171" }}>{errorMessage}</p>
                    {debugInfo && (
                      <p style={{ fontSize: 10, color: "#64748b", marginTop: 4, fontFamily: "monospace" }}>{debugInfo}</p>
                    )}
                  </div>
                </div>
              </div>

              {errorMessage.includes("Domaine non autorisé") && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .2 }}
                  style={{ background: "rgba(139,92,246,.08)", border: "1px solid rgba(139,92,246,.2)", borderRadius: 12, padding: "12px 14px", marginTop: 10 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: "#a78bfa", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                    <Globe size={12} /> Solution nécessaire
                  </p>
                  <ol style={{ fontSize: 11, color: "#64748b", paddingLeft: 16, lineHeight: 1.8 }}>
                    <li>Ouvrez Firebase Console → Authentication</li>
                    <li>Cliquez sur « Sign-in method »</li>
                    <li>Ajoutez : <code style={{ background: "rgba(255,255,255,.06)", padding: "1px 6px", borderRadius: 4, color: "#e2e8f0" }}>{hostname}</code></li>
                    <li>Cliquez Save et attendez 1–2 minutes</li>
                  </ol>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleEmailLogin} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <InputField
            id="email" label="Email Administrateur" type="email"
            value={email} onChange={e => setEmail(e.target.value.trim())}
            placeholder="admin@domaine.com"
            icon={Mail} disabled={anyLoading} autoFocus
          />

          <InputField
            id="current-password" label="Mot de passe" type={showPassword ? "text" : "password"}
            value={password} onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            icon={Lock} disabled={anyLoading}
            rightElement={
              <button type="button" onClick={() => setShowPassword(v => !v)} disabled={anyLoading}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", color: "#475569" }}>
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            }
          />

          <motion.button
            type="submit"
            disabled={anyLoading || !isFormValid}
            whileTap={(!anyLoading && isFormValid) ? { scale: .98 } : {}}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 12, border: "none",
              background: (!anyLoading && isFormValid) ? "linear-gradient(135deg,#f59e0b,#d97706)" : "rgba(245,158,11,.2)",
              color: (!anyLoading && isFormValid) ? "#fff" : "#64748b",
              fontSize: 13, fontWeight: 700, cursor: (!anyLoading && isFormValid) ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: (!anyLoading && isFormValid) ? "0 0 24px rgba(245,158,11,.25)" : "none",
              transition: "all .2s", marginTop: 2,
            }}
          >
            {loading
              ? <><div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", animation: "spin .7s linear infinite" }} />Vérification des accès…</>
              : <><Shield size={14} />Connexion Administrateur</>
            }
          </motion.button>
        </form>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.07)" }} />
          <span style={{ fontSize: 11, color: "#334155", fontWeight: 600 }}>OU</span>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.07)" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <motion.button
            type="button"
            onClick={() => handleSocialLogin("google")}
            disabled={anyLoading}
            whileTap={!anyLoading ? { scale: .97 } : {}}
            style={{ padding: "11px 0", borderRadius: 12, border: "1px solid rgba(255,255,255,.09)", background: "rgba(255,255,255,.06)", color: "#e2e8f0", fontSize: 12, fontWeight: 600, cursor: anyLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: anyLoading ? .5 : 1, transition: "background .15s" }}
            onMouseEnter={e => { if (!anyLoading) e.currentTarget.style.background = "rgba(255,255,255,.09)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.06)"; }}
          >
            {socialLoading.google
              ? <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,.2)", borderTopColor: "#e2e8f0", animation: "spin .7s linear infinite" }} />
              : <>
                  <svg width="15" height="15" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </>
            }
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleSocialLogin("github")}
            disabled={anyLoading}
            whileTap={!anyLoading ? { scale: .97 } : {}}
            style={{ padding: "11px 0", borderRadius: 12, border: "1px solid rgba(255,255,255,.09)", background: "rgba(255,255,255,.06)", color: "#e2e8f0", fontSize: 12, fontWeight: 600, cursor: anyLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: anyLoading ? .5 : 1, transition: "background .15s" }}
            onMouseEnter={e => { if (!anyLoading) e.currentTarget.style.background = "rgba(255,255,255,.09)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.06)"; }}
          >
            {socialLoading.github
              ? <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,.2)", borderTopColor: "#e2e8f0", animation: "spin .7s linear infinite" }} />
              : <><Github size={14} /> GitHub</>
            }
          </motion.button>
        </div>

        <div style={{ textAlign: "center", marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,.06)" }}>
          <p style={{ fontSize: 11, color: "#334155", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
            <Shield size={11} color="#475569" />
            Seuls les utilisateurs avec rôle <strong style={{ color: "#64748b" }}>"Admin"</strong> peuvent accéder
          </p>
          <p style={{ fontSize: 10, color: "#1e293b", marginTop: 6 }}>
            Panel Admin • v1.0 • {hostname}{isNative ? " • APK" : ""}
          </p>
        </div>
      </motion.div>

      <style>{`
        @media (min-width: 900px) {
          .login-left { display: flex !important; }
        }
      `}</style>
    </div>
  );
};

export default Login;